type RegionCatalogEntry = { id: string; name: string };

type CountryRegionSource = {
  regions?: unknown;
  regionNames?: string | string[] | null;
};

/** Normalizes `country.regions` refs to region id strings. */
export function extractCountryRegionIds(regions: unknown): string[] {
  if (!Array.isArray(regions)) {
    return [];
  }

  return regions
    .map((entry) => {
      if (typeof entry === "string") {
        return entry.trim();
      }
      if (entry && typeof entry === "object" && "id" in entry) {
        return String((entry as { id: unknown }).id ?? "").trim();
      }
      return "";
    })
    .filter(Boolean);
}

function normalizeStoredRegionNames(
  regionNames: CountryRegionSource["regionNames"],
): string {
  if (typeof regionNames === "string") {
    return regionNames.trim();
  }
  if (Array.isArray(regionNames)) {
    return regionNames
      .map((value) => String(value ?? "").trim())
      .filter(Boolean)
      .join(", ");
  }
  return "";
}

function resolveRegionNamesFromIds(
  regionIds: string[],
  regionCatalog: RegionCatalogEntry[],
): string {
  if (!regionIds.length) {
    return "";
  }

  const byId = new Map(regionCatalog.map((region) => [region.id, region.name]));
  const names = regionIds
    .map((id) => byId.get(id))
    .filter((name): name is string => Boolean(name));

  return names.join(", ");
}

/**
 * Resolves display region label for a country.
 * Prefers denormalized `country.regionNames` (same as AddFirm), then falls back to region id lookup.
 */
export function resolveCountryRegionNames(
  country: CountryRegionSource | null | undefined,
  regionCatalog: RegionCatalogEntry[],
): string {
  if (!country) {
    return "";
  }

  const stored = normalizeStoredRegionNames(country.regionNames);
  if (stored) {
    return stored;
  }

  const regionIds = extractCountryRegionIds(country.regions);
  return resolveRegionNamesFromIds(regionIds, regionCatalog);
}

export function compareCountryRegionLabels(
  country: CountryRegionSource,
  regionCatalog: RegionCatalogEntry[],
): {
  storedRegionNames: string;
  resolvedFromIds: string;
  regionIds: string[];
  mismatch: boolean;
} {
  const storedRegionNames = normalizeStoredRegionNames(country.regionNames);
  const regionIds = extractCountryRegionIds(country.regions);
  const resolvedFromIds = resolveRegionNamesFromIds(regionIds, regionCatalog);

  const mismatch =
    Boolean(storedRegionNames && resolvedFromIds) &&
    storedRegionNames.toLowerCase() !== resolvedFromIds.toLowerCase();

  return {
    storedRegionNames,
    resolvedFromIds,
    regionIds,
    mismatch,
  };
}
